import { useEffect, useState } from 'react';

const ESTIMATED_CHARS_PER_LINE = 70;

export type UseShowMoreTextProps = {
  originalText: string | undefined | null;
  maxChars?: number;
  maxLines?: number;
  showMoreInitialState?: boolean;
  handleShowMoreClick?: () => void;
  handleShowLessClick?: () => void;
};

export const useShowMoreText = ({
  originalText,
  maxChars,
  maxLines,
  showMoreInitialState = false,
  handleShowMoreClick,
  handleShowLessClick,
}: UseShowMoreTextProps) => {
  const [showingMore, setShowingMore] = useState(showMoreInitialState);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const [truncatedText, setTruncatedText] = useState<string>('');
  const [remainingText, setRemainingText] = useState<string>('');

  // Calculate the most restrictive character limit from maxChars and maxLines
  const calculateEffectiveMaxChars = (text: string): number => {
    const limits: number[] = [];

    if (maxChars) {
      limits.push(maxChars);
    }

    if (maxLines) {
      const lines = text.split('\n');
      if (lines.length > maxLines) {
        // Text has explicit line breaks
        const truncatedByLines = lines.slice(0, maxLines).join('\n');
        limits.push(truncatedByLines.length);
      } else {
        // Text is continuous - simple estimate
        const estimatedMaxChars = maxLines * ESTIMATED_CHARS_PER_LINE;
        if (text.length > estimatedMaxChars) {
          limits.push(estimatedMaxChars);
        }
      }
    }

    return limits.length > 0 ? Math.min(...limits) : text.length;
  };

  useEffect(() => {
    if (!originalText) return;

    const effectiveMaxChars = calculateEffectiveMaxChars(originalText);
    const truncated = originalText.substring(0, effectiveMaxChars);
    const needsTruncationCheck = truncated.length < originalText.length;

    if (needsTruncationCheck) {
      const trimmedTruncated = truncated.trimEnd();
      setTruncatedText(trimmedTruncated);
      setRemainingText(originalText.substring(trimmedTruncated.length));
    } else {
      setTruncatedText(originalText.trimEnd());
      setRemainingText('');
    }
    setNeedsTruncation(needsTruncationCheck);
  }, [originalText, maxChars, maxLines]);

  const handleToggle = () => {
    setShowingMore(!showingMore);
    !showingMore ? handleShowMoreClick?.() : handleShowLessClick?.();
  };

  return {
    showingMore,
    needsTruncation,
    truncatedText,
    remainingText,
    handleToggle,
  };
};
